import React, { useLayoutEffect, useRef, useState } from 'react';

export type ControlPopupAnimationState = 'enter' | 'exit';

interface ControlPopupPresenceProps {
  children: (animationState: ControlPopupAnimationState) => React.ReactNode;
  isOpen: boolean;
}

const EXIT_ANIMATION_DURATION_MS = 200;

const ControlPopupPresence: React.FC<ControlPopupPresenceProps> = ({ children, isOpen }) => {
  const [isRendered, setIsRendered] = useState(isOpen);
  const [animationState, setAnimationState] = useState<ControlPopupAnimationState>('enter');
  const exitTimerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (exitTimerRef.current !== null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (isOpen) {
      setIsRendered(true);
      setAnimationState('enter');
      return;
    }

    if (!isRendered) {
      return;
    }

    setAnimationState('exit');
    exitTimerRef.current = window.setTimeout(() => {
      exitTimerRef.current = null;
      setIsRendered(false);
    }, EXIT_ANIMATION_DURATION_MS);

    return () => {
      if (exitTimerRef.current !== null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [isOpen, isRendered]);

  if (!isRendered) {
    return null;
  }

  return children(animationState);
};

export default ControlPopupPresence;
